import styled from "styled-components";
import { useTranslate } from "hooks/useTranslate/";
import type { ThemeGroup } from "../../../storyboardEditor/components/ThemeSelector";
import type { ThemeResponse } from "../../hooks/useThemeApi";
import { ThemeCard } from "./ThemeCard";

type ThemeGalleryProps = {
  themeGroups: ThemeGroup[];
  onSelectTheme: (theme: ThemeResponse) => void;
};

/**
 * Browsable gallery shown in the editor body while no theme is selected.
 * Groups mirror the theme selector (My themes first, then public themes);
 * empty groups are skipped by the caller. Clicking a card opens the theme.
 */
export const ThemeGallery = ({
  themeGroups,
  onSelectTheme,
}: ThemeGalleryProps) => {
  const t = useTranslate();

  if (themeGroups.length === 0) return null;

  return (
    <StyledScroll>
      <StyledContainer>
        <StyledHeader>
          <StyledTitle>{t("media.themeEditor.gallery.title")}</StyledTitle>
          <StyledSubtitle>
            {t("media.themeEditor.gallery.subtitle")}
          </StyledSubtitle>
        </StyledHeader>

        {themeGroups.map((group) => (
          <StyledSection key={group.key}>
            <StyledSectionLabel>{group.label}</StyledSectionLabel>
            <StyledGrid>
              {group.themes.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  onSelect={onSelectTheme}
                />
              ))}
            </StyledGrid>
          </StyledSection>
        ))}
      </StyledContainer>
    </StyledScroll>
  );
};

// ─── Styled components ───

const StyledScroll = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 1280px;
  margin-inline: auto;
  padding: 40px 32px 64px;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledTitle = styled.h1`
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colorText};
`;

const StyledSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colorTextTertiary};
`;

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledSectionLabel = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;
